import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import type { FetchMoreQueryOptions, FetchMoreOptions } from "@apollo/client";
import type { LikedStoresQuery, LikedStoresQueryVariables } from "../../../__generated__/graphql";
import { getRestoredLikedStores } from "../utils/restore-liked-stores";

export type LikedStoreItem = NonNullable<
  NonNullable<LikedStoresQuery["likedStores"]>[number]
>;

interface UseInfiniteScrollLikedStoresOptions {
  limit?: number;
  hasLastLo: boolean;
  getLocationFromStorage: () => { lat: number; lng: number };
  /** API 초기 조회 완료 여부. skip된 경우(복원 데이터 사용) true로 전달 */
  queryLoading: boolean;
  fetchMore:
    | ((
        options: FetchMoreQueryOptions<LikedStoresQueryVariables, LikedStoresQuery> &
          FetchMoreOptions<LikedStoresQuery>
      ) => Promise<{ data: LikedStoresQuery }>)
    | null;
  storageKey?: string;
}

export const useInfiniteScrollLikedStores = ({
  limit = 20,
  hasLastLo,
  getLocationFromStorage,
  queryLoading,
  fetchMore,
  storageKey = "like",
}: UseInfiniteScrollLikedStoresOptions) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const fetchMoreRef = useRef(fetchMore);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);

  // fetchMore가 변경될 때마다 ref 업데이트
  useEffect(() => {
    fetchMoreRef.current = fetchMore;
  }, [fetchMore]);

  // 현재 위치 정보 (위치가 변경되면 저장된 데이터 무효화용)
  const currentLocationKey = useMemo(() => {
    const currentLat = hasLastLo ? getLocationFromStorage().lat : null;
    const currentLng = hasLastLo ? getLocationFromStorage().lng : null;
    return `${currentLat}-${currentLng}`;
  }, [hasLastLo, getLocationFromStorage]);

  // sessionStorage에서 복원
  const restoredData = getRestoredLikedStores(storageKey, currentLocationKey);

  // 위치가 변경되었을 때 저장된 데이터 무효화
  useEffect(() => {
    if (restoredData === null) {
      const savedLocationKey = sessionStorage.getItem(
        `${storageKey}:locationKey`
      );
      if (savedLocationKey != null && savedLocationKey !== currentLocationKey) {
        sessionStorage.removeItem(`${storageKey}:allStores`);
        sessionStorage.removeItem(`${storageKey}:offset`);
        sessionStorage.removeItem(`${storageKey}:hasMore`);
        sessionStorage.removeItem(`${storageKey}:locationKey`);
      }
    }
    // 위치 키 변경 시에만 스토리지 정리. restoredData는 의도적으로 제외(매 렌더 새 참조로 불필요 실행 방지)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocationKey, storageKey]);

  const [offset, setOffset] = useState(restoredData?.offset ?? 0);
  const [allStores, setAllStores] = useState<LikedStoreItem[]>(
    (restoredData?.stores as LikedStoreItem[]) ?? []
  );
  const [hasMore, setHasMore] = useState(restoredData?.hasMore ?? true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initLoading, setInitLoading] = useState(!restoredData);

  // allStores 변경 시 sessionStorage에 저장
  useEffect(() => {
    if (allStores && allStores.length > 0) {
      try {
        sessionStorage.setItem(
          `${storageKey}:allStores`,
          JSON.stringify(allStores)
        );
        sessionStorage.setItem(`${storageKey}:offset`, offset.toString());
        sessionStorage.setItem(`${storageKey}:hasMore`, hasMore.toString());
        sessionStorage.setItem(`${storageKey}:locationKey`, currentLocationKey);
      } catch (error) {
        console.error("Failed to save to storage:", error);
      }
    }
  }, [allStores, offset, hasMore, currentLocationKey, storageKey]);

  // 복원된 데이터가 있거나 API 초기 조회가 완료되면 로딩 완료 처리
  useEffect(() => {
    if (restoredData && restoredData.stores.length > 0) {
      setInitLoading(false);
    } else if (!restoredData && !queryLoading) {
      // API 조회 완료 (데이터 유무와 관계없이)
      setInitLoading(false);
    } else if (!restoredData && queryLoading) {
      setInitLoading(true);
    }
  }, [restoredData, queryLoading]);

  // 위치가 변경되면 저장된 데이터 무효화 및 상태 초기화
  useEffect(() => {
    const savedLocationKey = sessionStorage.getItem(
      `${storageKey}:locationKey`
    );
    if (savedLocationKey && savedLocationKey !== currentLocationKey) {
      // 위치가 변경되었으므로 상태 초기화
      setOffset(0);
      setAllStores([]);
      setHasMore(true);
      setInitLoading(true);
      setHasUserScrolled(false);
    }
  }, [
    currentLocationKey,
    storageKey,
    setAllStores,
    setOffset,
    setHasMore,
    setInitLoading,
  ]);

  // 더 많은 매장 불러오기 함수
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || initLoading || !fetchMoreRef.current)
      return;

    setIsLoadingMore(true);

    try {
      const result = await fetchMoreRef.current({
        variables: {
          lat: hasLastLo ? getLocationFromStorage().lat : null,
          lng: hasLastLo ? getLocationFromStorage().lng : null,
          offset: offset,
          limit: limit,
        },
      });

      if (result.data?.likedStores) {
        const newStores = result.data.likedStores;

        // 중복 제거하여 추가
        setAllStores((prev) => {
          const existingIds = new Set(
            prev
              .map((store) => store?.id)
              .filter((id): id is number => id != null)
          );
          const uniqueNewStores = newStores.filter(
            (store: LikedStoreItem) =>
              store && store.id != null && !existingIds.has(store.id)
          );
          return [...prev, ...uniqueNewStores];
        });

        setOffset((prev) => prev + newStores.length);
        setHasMore(newStores.length === limit);
      }
    } catch (error) {
      console.error("Failed to load more stores:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    isLoadingMore,
    hasMore,
    initLoading,
    hasLastLo,
    getLocationFromStorage,
    offset,
    limit,
  ]);

  // 사용자 스크롤 감지
  useEffect(() => {
    const scrollContainer = rootRef.current;
    if (!scrollContainer || initLoading) return;

    let lastScrollTop = scrollContainer.scrollTop;

    const handleScroll = () => {
      const currentScrollTop = scrollContainer.scrollTop;
      // 스크롤 위치가 변경되었고, 프로그래밍 방식이 아닌 사용자 스크롤인 경우
      if (Math.abs(currentScrollTop - lastScrollTop) > 5) {
        setHasUserScrolled(true);
      }
      lastScrollTop = currentScrollTop;
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [allStores, initLoading]);

  // Intersection Observer로 무한 스크롤 구현
  useEffect(() => {
    // 초기 로딩 중이거나, 더 이상 불러올 데이터가 없거나, 로딩 중이거나, 데이터가 없으면 observer 등록하지 않음
    if (
      initLoading ||
      !hasMore ||
      isLoadingMore ||
      !allStores ||
      allStores.length === 0
    )
      return;

    // 초기 로드 직후에는 observer를 바로 등록하지 않고, 약간의 딜레이를 둠 (1초 후 활성화)
    // 또는 사용자가 스크롤한 경우 즉시 활성화
    if (!hasUserScrolled) {
      const timeout = setTimeout(() => {
        setHasUserScrolled(true);
      }, 1000);
      return () => clearTimeout(timeout);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoadingMore && !initLoading) {
          handleLoadMore();
        }
      },
      {
        root: rootRef.current,
        rootMargin: "300px",
      }
    );

    const target = loadMoreRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasMore,
    isLoadingMore,
    handleLoadMore,
    allStores.length,
    initLoading,
    hasUserScrolled,
  ]);

  return {
    rootRef,
    loadMoreRef,
    allStores,
    setAllStores,
    offset,
    setOffset,
    hasMore,
    setHasMore,
    isLoadingMore,
    initLoading,
    setInitLoading,
    restoredData,
  };
};
